import { useState } from 'react';
import { LiveMap } from '@/components/dispatcher/LiveMap';
import { ActiveFleetBar } from '@/components/dispatcher/ActiveFleetBar';
import { UnclosedDwellPanel } from '@/components/dispatcher/UnclosedDwellPanel';
import { LoadBalancePanel } from '@/components/dispatcher/LoadBalancePanel';
import { AssignmentPlanPanel } from '@/components/dispatcher/AssignmentPlanPanel';
import { DayBoard } from '@/components/dispatcher/DayBoard';
import { QuickDispatchModal } from '@/components/dispatcher/QuickDispatchModal';
import { MapBottomSheet } from '@/components/dispatcher/MapBottomSheet';
import { MD_BREAKPOINT_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { SetupChecklist } from '@/components/setup/SetupChecklist';
import { WorklistPanel } from '@/components/billing/WorklistPanel';

/**
 * Two layouts, because the map and the fleet list cannot both be useful at
 * once on a phone.
 *
 * Desktop stacks them: the map full-width above the live tracking list. It
 * was a side-by-side grid until a dispatcher pointed out that the half a
 * screen a quarry map gets in a two-column layout is not enough to see
 * where the fleet actually is. On a narrow screen the map moves into a
 * draggable bottom sheet and the fleet list gets the page — see
 * MapBottomSheet for why it snaps rather than free-drags.
 *
 * The branch is a real media query rather than `hidden md:block`, and that
 * distinction is load-bearing here: Tailwind's breakpoints only change
 * visibility, so writing both layouts into the tree mounted TWO Leaflet
 * maps — two tile fetches, two marker sets, two live subscriptions, one of
 * them invisible on every screen size. Exactly one map instance exists now.
 * See hooks/useMediaQuery.js.
 */
export function DispatcherView() {
  const [quickDispatchOpen, setQuickDispatchOpen] = useState(false);
  const isDesktop = useMediaQuery(MD_BREAKPOINT_QUERY);

  return (
    <>
      {isDesktop ? (
        <div className="space-y-4">
          {/* Above the map on purpose, and above UnclosedDwellPanel: on a
              half-configured install the map is empty and the checklist is
              the only thing on this screen with anything to say. It renders
              nothing once every step is done, so it costs a finished
              install no space at all. */}
          <SetupChecklist />
          {/* Under the setup checklist and above the map, on the landing
              screen, because the person this list is for is not going to go
              looking for it. It renders nothing at all when there is nothing
              owed, so a well-run week costs no space. */}
          <WorklistPanel />
          <LiveMap />
          {/* Above the live list on purpose: a stop nobody closed is not
              something happening now, it is something already wrong, and it
              stopped raising the live alarm the moment it crossed the
              plausibility bound. If it sat below the fold it would be
              invisible on exactly the days it matters. */}
          <UnclosedDwellPanel />
          <ActiveFleetBar onQuickDispatch={() => setQuickDispatchOpen(true)} />
          {/* Under the live list, not above it: that strip answers
              "what is happening right now", which is what a dispatcher
              opens this screen for. This one answers "how does the rest
              of the day look", which is the question they ask second —
              and it is a whole-fleet comparison, so it wants the full
              width rather than a slot beside the map. */}
          <LoadBalancePanel />
          {/* Beside the balance, not above the live list: the balance
              shows the imbalance, this proposes the fix. */}
          <AssignmentPlanPanel />
          {/* Trecias, ir tvarka yra prasme: kruvio balansas parodo
              NELYGYBE, planuotojas siulo SIOS DIENOS taisyma, o si lenta
              atsako i klausima, kurio ne vienas is ju neuzduoda — "kas
              pazadeta TAI dienai, ir kas is to dar be vairuotojo".
              Dispeceris ja atsiverčia planuodamas priekin, ne gesindamas
              siandiena. */}
          <DayBoard />
        </div>
      ) : (
        <div className="space-y-3">
          <SetupChecklist />
          {/* Under the setup checklist and above the map, on the landing
              screen, because the person this list is for is not going to go
              looking for it. It renders nothing at all when there is nothing
              owed, so a well-run week costs no space. */}
          <WorklistPanel />
          <MapBottomSheet
            // Leaflet sizes its canvas to the container and does not notice a
            // parent that changed height. It does listen for window resize
            // (trackResize is on by default), so this is the one-line way to
            // make it re-measure without reaching into the map instance.
            onHeightChange={() => window.dispatchEvent(new Event('resize'))}
          >
            <LiveMap fill />
          </MapBottomSheet>
          <UnclosedDwellPanel />
          <ActiveFleetBar onQuickDispatch={() => setQuickDispatchOpen(true)} />
          {/* Under the live list, not above it: that strip answers
              "what is happening right now", which is what a dispatcher
              opens this screen for. This one answers "how does the rest
              of the day look", which is the question they ask second —
              and it is a whole-fleet comparison, so it wants the full
              width rather than a slot beside the map. */}
          <LoadBalancePanel />
          {/* Beside the balance, not above the live list: the balance
              shows the imbalance, this proposes the fix. */}
          <AssignmentPlanPanel />
        </div>
      )}

      <QuickDispatchModal open={quickDispatchOpen} onClose={() => setQuickDispatchOpen(false)} />
    </>
  );
}
